import { useQuery } from '@apollo/client'
import { TitledPaper } from '@dai/components'
import { VerifyEmail } from '@dai/firebase-auth'
import { Alert, Button, CircularProgress } from '@mui/material'
import { blue } from '@mui/material/colors'
import { useNavigate } from 'react-router-dom'

import CompanySetUp from '@/components/account-set-up/steps/CompanySetUp'
import Congrats from '@/components/account-set-up/steps/Congrats'
import FindAndCreateStores from '@/components/account-set-up/steps/FindAndCreateStores'
import InviteUsers from '@/components/account-set-up/steps/InviteUsers'
import PaymentSuccess from '@/components/account-set-up/steps/PaymentSuccess'
import SelectPlan from '@/components/account-set-up/steps/pricing/SelectPlan'
import ProfileSetUp from '@/components/account-set-up/steps/ProfileSetUp'
import { AccountSetUpStepProps } from '@/components/account-set-up/types'
import { useAccountSetUp } from '@/components/account-set-up/use-account-set-up'
import { useAccountSetUpNavigation } from '@/components/account-set-up/use-account-set-up-navigation'
import CenteredBox from '@/components/CenteredBox'
import {
  GET_TENANT_STORES,
  GetTenantStoresQuery,
  GetTenantStoresQueryVariables,
} from '@/graphql'
import useAuth from '@/hooks/useAuth'
import useLoggedInUser from '@/hooks/useLoggedInUser'
import useT from '@/i18n/useT'

function AccountSetUp() {
  const loggedInUser = useLoggedInUser()
  const auth = useAuth()
  const {
    user: authUser,
    isAuthenticated,
    sendEmailVerification,
    verifyEmail,
    signOut,
  } = auth
  const tenantStores = useQuery<
    GetTenantStoresQuery,
    GetTenantStoresQueryVariables
  >(GET_TENANT_STORES, {
    variables: {
      pagination: {
        offset: 0,
        limit: 10,
      },
    },
  })

  const accountSetUp = useAccountSetUp({
    loggedInUser,
    auth,
    tenantStores,
  })

  const { flow, skipPayment, paymentSuccess, loading } = accountSetUp

  const { currentStep, nextStep, isValid } = useAccountSetUpNavigation({
    ...accountSetUp,
    auth,
    loggedInUser,
    tenantStores,
    skipPayment,
    paymentSuccess,
  })

  const navigate = useNavigate()
  const { t } = useT()

  if (loading) {
    return (
      <CenteredBox>
        <CircularProgress />
      </CenteredBox>
    )
  }

  if (!loggedInUser.user) {
    return (
      <TitledPaper
        title={t('accountSetUp.title')}
        subtitle={
          <>
            {t('accountSetUp.subtitle.issueFindingUser')}{' '}
            <span style={{ color: blue[500] }}>
              {authUser?.email || t('accountSetUp.subtitle.unknownAccount')}
            </span>
          </>
        }
      >
        <Alert severity="warning">
          {t('accountSetUp.alert.issueFindingUser')}
          {loggedInUser.error?.message && `: ${loggedInUser.error.message}`}
        </Alert>
        <Button variant="outlined" color="primary" onClick={() => signOut()}>
          {t('accountSetUp.button.signOut')}
        </Button>
      </TitledPaper>
    )
  }

  const roles = loggedInUser.user?.groups.map(g => g.name) || []

  if (!flow.length) {
    return (
      <TitledPaper
        title={t('accountSetUp.title')}
        subtitle={
          <>
            {t('accountSetUp.subtitle.part1')}
            <span style={{ color: blue[500] }}>{loggedInUser.user.email}</span>
          </>
        }
      >
        <Alert severity="warning">
          {roles.length
            ? t('accountSetUp.alert.rolesAssigned', { roles: roles.join(', ') })
            : t('accountSetUp.alert.noRoles')}
        </Alert>
        <Button variant="outlined" color="primary" onClick={() => signOut()}>
          {t('accountSetUp.button.signOut')}
        </Button>
      </TitledPaper>
    )
  }

  if (!isValid) {
    return (
      <CenteredBox>
        <CircularProgress />
      </CenteredBox>
    )
  }

  const props: AccountSetUpStepProps = {
    user: loggedInUser.user,
    nextStep,
  }

  const renderStep = () => {
    switch (currentStep) {
      case 'verify-email':
        return (
          <VerifyEmail
            oobCode={null}
            continueUrl={'/account-set-up'}
            signInUrl={'/auth/sign-in'}
            navigateToUpdateEmail={() =>
              navigate('/auth/action?mode=verifyAndChangeEmail', {
                state: { from: '/account-set-up' },
              })
            }
            sendEmailVerification={sendEmailVerification}
            verifyEmail={verifyEmail}
            user={authUser}
            isAuthenticated={isAuthenticated}
          />
        )
      case 'profile-set-up':
        return <ProfileSetUp {...props} />
      case 'company-set-up':
        return <CompanySetUp {...props} />
      case 'find-stores':
        return <FindAndCreateStores selectable {...props} />
      case 'create-stores':
        return (
          <FindAndCreateStores
            refetchStores={tenantStores.refetch}
            selectable={false}
            {...props}
          />
        )
      case 'select-plan':
        return <SelectPlan />
      case 'payment-success':
        return <PaymentSuccess {...props} />
      case 'invite-users':
        return <InviteUsers {...props} />
      case 'congrats':
        return <Congrats {...props} />
      default:
        return t('accountSetUp.invalidStep', { step: currentStep })
    }
  }

  return <>{renderStep()}</>
}

export default AccountSetUp
